/* eslint-disable reverb-design-system/no-mixing-bem-and-utility-classes */
import React, { useEffect, useMemo } from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';

import { get } from 'lodash';
import { CSP } from '../../../schemas/rql';
import { echidnaUriTemplate } from '../../shared/url_helpers';

import { IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import LightboxImage from '@reverbdotcom/commons/src/components/lightbox_image';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import SpecList from '@reverbdotcom/commons/src/components/spec_list';
import VideoEmbed from '@reverbdotcom/commons/src/components/video_embed';

import {
  JUMPLINK_BLURB,
  JUMPLINK_GALLERY,
  JUMPLINK_SPECS,
  JUMPLINK_OVERVIEW,
  JUMPLINK_VIDEO,
  JUMPLINK_PRODUCT_DETAILS,
} from './csp_jumplinks';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

const IMAGE_LARGE = 'large_images';
const IMAGE_SUPERSIZE = 'supersize_images';
const MAX_THUMBNAILS = 12;

function getCspImages(csp, transform) {
  // lodash's 'get' only falls back if the result === undefined
  return get(csp, transform) || [];
}

interface GalleryProps {
  largeImages: string[];
  supersizeImages: string[];
  cspTitle: string;
}

function Gallery({ largeImages, supersizeImages, cspTitle }: GalleryProps) {
  const imageAlts = largeImages.map((_, index) => (
    I18n.t('discovery.comparisonShoppingPages.imageAlt', { title: cspTitle, index: index + 1 })
  ));

  return (
    <div
      className="csp2-product-module__section csp2-product-module__section--gallery"
      id={JUMPLINK_GALLERY}
    >
      <h3 className="csp2-product-module__section--header">
        {I18n.t('discovery.comparisonShoppingPages.jumpLinks.gallery')}
      </h3>
      <LightboxImage
        data-lightbox-image
        thumbURLs={largeImages}
        imageURLs={largeImages}
        imageAlts={imageAlts}
        fullsizeURLs={supersizeImages}
        withControls
      />
    </div>
  );
}

function Specs({ specs }) {
  return (
    <div
      className="csp2-product-module__section csp2-product-module__section--specs"
      id={JUMPLINK_SPECS}
    >
      <h3 className="csp2-product-module__section--header">
        {I18n.t('discovery.comparisonShoppingPages.jumpLinks.specs')}
      </h3>
      <div
        className="csp2-product-module__specs-list" //right BEM classname?
      >
        <SpecList specs={specs} collapseList />
      </div>
    </div>
  );
}

function ProductOverview({ overview, body = '' }: { overview: string, body: string }) {
  return (
    <div
      className="csp2-product-module__section csp2-product-module__section--overview"
      id={JUMPLINK_OVERVIEW}
    >
      <h3 className="csp2-product-module__section--overview-header">
        {I18n.t('discovery.comparisonShoppingPages.jumpLinks.overview')}
      </h3>
      <div
        className="csp2-product-module__description"
      >
        <SanitizedRender
          htmlTag="div"
          html={overview}
        />
        {!!body &&
          <a
            href={`#${JUMPLINK_BLURB}`}
            className="csp2-product-module__overview-read-more"
          >
            {I18n.t('discovery.comparisonShoppingPages.readMore')}
          </a>
        }
      </div>
    </div>
  );
}

function getVideoSlugs(csp: CSP) {
  const slugs = csp?.videos?.videoLinks || [];
  return slugs.map(s => s?.youtubeVideoId);
}

function Video({ videos }: { videos: string[] }) {
  return (
    <div
      className="csp2-product-module__section csp2-product-module__section--video"
      id={JUMPLINK_VIDEO}
    >
      <h3 className="csp2-product-module__section--header">
        {I18n.t('discovery.comparisonShoppingPages.jumpLinks.video')}
      </h3>
      <div className="csp2-product-module__video-wrapper">
        <VideoEmbed slug={videos[0]} thumbnailSlugs={videos} />
      </div>
    </div>
  );
}

function AdminSection({ cspSlug }: { cspSlug: string }) {
  const echidnaUri = echidnaUriTemplate.expand({
    section: 'product-matching',
    query: {
      csp_slug: cspSlug,
      condition: '',
      show_matched: true,
      show_unmatched: false,
      hidden: true,
      showsold: true,
    },
  });

  return (
    <div className="csp2-product-module__section csp2-product-module__section--admin">
      <h3 className="item2__sidebar-module__header icon-r-lock">
        {I18n.t('discovery.adminButtons.adminActions')}
      </h3>
      <a
        href={echidnaUri}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="mt-2 button button--small width-100"
      >
        {I18n.t('discovery.adminButtons.openInEchidna')}
      </a>
    </div>
  );
}

interface IProps extends IUserContext {
  csp: CSP;
  loading?: boolean;
  setJumplinks?: Function;
}

export function CSPProductModule({ csp, loading = false, setJumplinks = undefined }: IProps) {
  const user = useUser();
  const largeImages = getCspImages(csp, IMAGE_LARGE).map(img => img.source).slice(0, MAX_THUMBNAILS);
  const supersizeImages = getCspImages(csp, IMAGE_SUPERSIZE).map(img => img.source);
  const hasGalleryImages = !!(largeImages.length + supersizeImages.length);

  const videos = getVideoSlugs(csp);
  const overview = csp?.summary;
  const specs = useMemo(() => csp?.specs?.productSpecs || [], [csp]);

  useEffect(() => {
    setJumplinks({
      [JUMPLINK_VIDEO]: !!videos.length,
      [JUMPLINK_GALLERY]: hasGalleryImages,
      [JUMPLINK_OVERVIEW]: !!overview,
      [JUMPLINK_SPECS]: !!specs.length,
    });
  }, [csp, setJumplinks, videos, overview, specs, hasGalleryImages]);

  if (!csp) { return null; }
  const classes = classNames(
    'csp2-product-module',
    { 'csp2-product-module--loading': loading },
  );

  return (
    <div className={classes} id={JUMPLINK_PRODUCT_DETAILS}>
      <h2 className="csp2-product-module__header">{I18n.t('discovery.comparisonShoppingPages.productDetails')}</h2>
      <div className="csp2-product-module__sections">
        {hasGalleryImages && <Gallery largeImages={largeImages} supersizeImages={supersizeImages} cspTitle={csp.title} />}
        {!!specs.length && <Specs specs={specs} />}
        {!!overview && <ProductOverview overview={overview} body={csp.body} />}
        {user.isAdmin && <AdminSection cspSlug={csp.slug} />}
        {!!videos.length && <Video videos={videos} />}
      </div>
    </div>
  );
}
